import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2, ShieldCheck, ShieldAlert, X, Star, RefreshCw, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// FMCSA carrier vetting for dispatchers: quick DOT/MC lookup + a saved list of
// vetted brokers/carriers that can be re-checked in one click.
const CarrierCheckWidget = ({ onClose }) => {
  const [mode, setMode] = useState('dot');
  const [val, setVal] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState([]);
  const [busyId, setBusyId] = useState(null);

  const loadSaved = async () => {
    try {
      const res = await axios.get(`${API}/broker-carriers`, { withCredentials: true });
      setSaved(res.data || []);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { loadSaved(); }, []);

  const lookup = async () => {
    setError(''); setResult(null);
    if (!/^\d+$/.test(val.trim())) { setError(`Enter a numeric ${mode.toUpperCase()} number.`); return; }
    setLoading(true);
    try {
      const path = mode === 'mc' ? `fmcsa/carrier-by-mc/${val.trim()}` : `fmcsa/carrier/${val.trim()}`;
      const res = await axios.get(`${API}/${path}`, { withCredentials: true });
      setResult(res.data);
    } catch (e) {
      const code = e.response?.status;
      setError(code === 404 ? `No carrier found for ${mode.toUpperCase()} #${val.trim()}.`
        : code === 503 ? 'FMCSA lookup is unavailable right now.'
        : (e.response?.data?.detail || 'Lookup failed.'));
    } finally { setLoading(false); }
  };

  const save = async () => {
    if (!result) return;
    setSaving(true);
    try {
      const body = mode === 'mc' ? { mc_number: val.trim() } : { dot_number: val.trim() };
      await axios.post(`${API}/broker-carriers`, body, { withCredentials: true });
      await loadSaved();
    } catch (e) { setError(e.response?.data?.detail || 'Could not save carrier.'); }
    finally { setSaving(false); }
  };

  const recheck = async (id) => {
    setBusyId(id);
    try {
      const res = await axios.post(`${API}/broker-carriers/${id}/recheck`, {}, { withCredentials: true });
      setSaved((prev) => prev.map((c) => (c.id === id ? res.data : c)));
    } catch (e) { /* ignore */ } finally { setBusyId(null); }
  };

  const remove = async (id) => {
    setBusyId(id);
    try {
      await axios.delete(`${API}/broker-carriers/${id}`, { withCredentials: true });
      setSaved((prev) => prev.filter((c) => c.id !== id));
    } catch (e) { /* ignore */ } finally { setBusyId(null); }
  };

  const isGood = (c) => !c.out_of_service && c.insurance_ok !== false;
  const alreadySaved = result && saved.some((c) => c.dot_number === result.dot_number);

  return (
    <div className="bg-white border border-slate-200 rounded-md p-5 mb-6" data-testid="carrier-check-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Vet a Carrier (FMCSA)</h3>
        {onClose && (
          <button data-testid="carrier-check-close" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
        )}
      </div>

      <div className="flex items-center gap-2 mb-2">
        {['dot', 'mc'].map((m) => (
          <button
            key={m}
            data-testid={`carrier-check-mode-${m}`}
            onClick={() => { setMode(m); setResult(null); setError(''); }}
            className={`px-2.5 py-0.5 rounded-full text-[11px] font-semibold border transition-all ${mode === m ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white text-slate-500 border-slate-300'}`}
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            {m === 'dot' ? 'US DOT #' : 'MC #'}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          data-testid="carrier-check-input"
          value={val}
          onChange={(e) => setVal(e.target.value.replace(/\D/g, ''))}
          onKeyDown={(e) => e.key === 'Enter' && lookup()}
          placeholder={mode === 'mc' ? 'e.g. 1515' : 'e.g. 44110'}
          className="flex-1 px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-transparent focus:outline-none"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        />
        <button
          data-testid="carrier-check-lookup"
          onClick={lookup}
          disabled={loading || !val}
          className="px-3 py-2 rounded-md bg-[#002FA7] text-white text-sm font-semibold hover:bg-[#00247D] transition-all disabled:opacity-40 flex items-center gap-1.5"
          style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          Check
        </button>
      </div>
      {error && <p data-testid="carrier-check-error" className="text-xs text-red-600 mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</p>}

      {result && (
        <div data-testid="carrier-check-result" className={`mt-3 rounded-md border px-3 py-2.5 ${isGood(result) ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2">
              {isGood(result) ? <ShieldCheck className="w-4 h-4 text-green-600" /> : <ShieldAlert className="w-4 h-4 text-red-600" />}
              <span className={`text-sm font-bold ${isGood(result) ? 'text-green-800' : 'text-red-800'}`} data-testid="carrier-check-status">{result.operating_status}</span>
            </div>
            <button
              data-testid="carrier-check-save"
              onClick={save}
              disabled={saving || alreadySaved}
              className="text-xs font-semibold text-[#002FA7] hover:underline disabled:text-slate-400 disabled:no-underline flex items-center gap-1"
            >
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Star className="w-3 h-3" />}
              {alreadySaved ? 'Saved' : 'Save carrier'}
            </button>
          </div>
          <div className="text-sm text-slate-800 font-semibold">{result.legal_name}</div>
          <div className="text-xs text-slate-500">DOT #{result.dot_number}{result.mc_number ? ` · MC #${result.mc_number}` : ''} · {[result.phy_city, result.phy_state].filter(Boolean).join(', ')}</div>
          {result.insurance_issues?.length > 0 && (
            <div className="text-xs text-red-700 mt-1">Insurance: {result.insurance_issues.join('; ')}</div>
          )}
        </div>
      )}

      {saved.length > 0 && (
        <div className="mt-4" data-testid="saved-carriers">
          <h4 className="text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Saved carriers</h4>
          <ul className="border border-slate-200 rounded-md divide-y divide-slate-100">
            {saved.map((c) => (
              <li key={c.id} className="flex items-center gap-3 px-3 py-2" data-testid={`saved-carrier-${c.dot_number}`}>
                {isGood(c) ? <ShieldCheck className="w-4 h-4 text-green-600 flex-shrink-0" /> : <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-800 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{c.legal_name}</div>
                  <div className="text-[11px] text-slate-500 truncate" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    DOT #{c.dot_number} · <span className={isGood(c) ? 'text-green-700' : 'text-red-700'}>{c.operating_status}</span>
                    {!c.insurance_ok ? ' · insurance issue' : ''} · checked {c.last_checked ? new Date(c.last_checked).toLocaleDateString() : ''}
                  </div>
                </div>
                <button data-testid={`recheck-carrier-${c.dot_number}`} onClick={() => recheck(c.id)} disabled={busyId === c.id} title="Re-check now" className="text-slate-400 hover:text-[#002FA7] disabled:opacity-40">
                  {busyId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                </button>
                <button data-testid={`delete-carrier-${c.dot_number}`} onClick={() => remove(c.id)} disabled={busyId === c.id} title="Remove" className="text-slate-400 hover:text-red-600 disabled:opacity-40">
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default CarrierCheckWidget;
